import { Component, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { TabVm } from 'qp-ui-interface';
import { takeUntil } from 'rxjs/operators';
import { AppEventName } from 'src/app/analytics/newrelic/enums/EventName';
import { PageType } from 'src/app/analytics/newrelic/enums/PageType';
import { NewrelicService } from 'src/app/analytics/newrelic/services/newrelic.service';
import { DataService } from 'src/app/data.service';
import { LanguageService } from 'src/app/language.service';
import { VerticalMenuEvent } from 'src/app/shared/common/interfaces/common.interface';
import { SubscriptionAction } from 'src/app/shared/enums/subscription';
import { LocalStorageKeys } from 'src/app/shared/LocalStorageKeys';
import { AuthService } from 'src/app/shared/services/auth/auth.service';
import { Destroyable } from 'src/app/shared/services/destroyable';
import { DialogService } from 'src/app/shared/services/dialog.service';
import { AccountProfileService } from 'src/app/shared/services/evergent/account/account-profile.service';
import { UserEntitlementService } from 'src/app/shared/services/evergent/entitlement/user-entitlement.service';
import { NavigationService } from 'src/app/shared/services/navigation.service';
import { NavigationLibraryService } from 'src/app/shared/services/navigation/navigation.service';
import { LocalStorageService } from 'src/app/shared/services/storage/local-storage.service';
import { TabService } from 'src/app/shared/services/tabs/tab.service';
import { GenericAhaGoldPromptComponent } from 'src/app/shared/shared-components/aha-upgrade-prompts/generic/generic-aha-gold-prompt/generic-aha-gold-prompt.component';
import { ExitAppComponent } from 'src/app/shared/shared-components/exit-app-and-sign-out/exit-app/exit-app.component';
import { SimplePlayerService } from 'src/app/shared/shared-components/simple-player/simple-player.service';
import { KEYS } from 'src/app/shared/utils/constants/storage.constants';
import { FocusRestorationService } from '../../shared/services/utility/focus-restoration.service';
import {
  MENU_ITEM_ICONS,
  MENU_ITEM_IDS,
  MENU_ITEM_NAMES,
  MENU_ITEMS,
} from './tv-vertical-menu.constants';
import { removeSplashScreen } from 'src/app/shared/utils/browserUtils';

@Component({
  selector: 'app-tv-vertical-menu',
  templateUrl: './tv-vertical-menu.component.html',
  styleUrls: ['./tv-vertical-menu.component.scss'],
})
export class TvVerticalMenuComponent
  extends Destroyable
  implements OnInit, OnDestroy
{
  readonly MENU_ITEM_IDS = MENU_ITEM_IDS;
  readonly MENU_ITEM_NAMES = MENU_ITEM_NAMES;
  readonly MENU_ITEM_ICONS = MENU_ITEM_ICONS;

  menuItems: TabVm[] = null;

  isMenuOpen = false;
  isMenuActive = false;
  showVerticalNav = true;
  selectedIndex: number | null = null;
  focusedIndex: number | null = null;
  lastFocusedIndex: number | null = null;
  isNavActive = false;

  showPlanAction: SubscriptionAction | null = null;
  internalIndex: number;
  lang = 'en';
  private restrictBack = false;
  private lastFocusedClass: { element: string; index: number } = null;

  constructor(
    private router: Router,
    private readonly authService: AuthService,
    private readonly tabService: TabService,
    private readonly navService: NavigationLibraryService,
    private readonly navigationService: NavigationService,
    private readonly languageService: LanguageService,
    private readonly accountProfileService: AccountProfileService,
    private readonly localStorage: LocalStorageService,
    private readonly dataService: DataService,
    private readonly userEntitlementService: UserEntitlementService,
    private readonly dialogService: DialogService,
    private readonly focusRestorationService: FocusRestorationService,
    private readonly simplePlayerService: SimplePlayerService,
    private readonly newrelic: NewrelicService
  ) {
    super();
  }

  ngOnInit(): void {
    this.lang = this.languageService.currLang;

    removeSplashScreen();

    this.userEntitlementService.planActionBtn$
      .pipe(takeUntil(this.destroy$))
      .subscribe((action) => {
        this.showPlanAction = action;
        this.updateMenu();
      });

    this.accountProfileService.selectedProfileObs
      .pipe(takeUntil(this.destroy$))
      .subscribe((profile) => {
        this.updateMenu();
      });

    this.dataService.verticalMenuEvent
      .pipe(takeUntil(this.destroy$))
      .subscribe((event) => {
        this.handleVerticalMenuEvent(event);
      });

    this.updateMenu();
    this.updateMenuItems();
  }

  private initializeMenu(): void {
    if (this.authService.isLoggedIn()) {
      this.menuItems = this.menuItems.filter(
        (item) => item.id !== MENU_ITEM_IDS.LINK_TV
      );
    }
  }

  private updateMenu(): void {
    const profile = this.accountProfileService.selectedProfile;
    this.menuItems = [...MENU_ITEMS];

    this.initializeMenu();

    if (!this.showPlanAction) {
      this.menuItems = this.menuItems.filter(
        (item) => item.id !== MENU_ITEM_IDS.SUBSCRIBE
      );
    }

    this.setTabs();
    this.menuItems.forEach((it) => {
      if (it.name === MENU_ITEM_NAMES.MY_AHA) {
        if (profile) {
          it.icon = 'my-aha-profile-icon-' + profile.index;
        } else {
          it.icon = MENU_ITEM_ICONS.MY_AHA;
        }
      }
    });
  }

  private setTabs() {
    const tabs = this.tabService.getTabs();
    tabs.forEach((item) => {
      let menuName: string = item.name[this.lang] || item.name['en'];
      let menuIcon: string = MENU_ITEM_ICONS.MOVIES;

      switch (item.name['en']?.toLowerCase()) {
        case 'all':
        case 'home':
          menuName = MENU_ITEM_NAMES.HOME;
          menuIcon = MENU_ITEM_ICONS.HOME;
          break;
        case 'shows':
          menuIcon = MENU_ITEM_ICONS.SHOWS;
          break;

        case 'watch for free':
          menuIcon = MENU_ITEM_ICONS.FREE;
          break;

        default:
          menuIcon = MENU_ITEM_ICONS.MOVIES;
      }

      this.menuItems.push({
        name: menuName,
        icon: menuIcon,
        id: item.id,
      });
    });
  }

  private updateMenuItems(): void {
    const previousEvent = this.dataService.verticalMenuLastEvent;

    if (previousEvent) {
      const { data } = previousEvent;
      if (this.dataService.isIntermediateLogin) {
        this.dataService.isIntermediateLogin = false;
        data.selectedIndex =
          data.selectedIndex -
          (this.menuItems.find((item) => item.id === MENU_ITEM_IDS.SUBSCRIBE)
            ? 1
            : 2);
      }
      if (
        data.selectedIndex > -1 &&
        (this.menuItems[data.selectedIndex].id === MENU_ITEM_IDS.MY_AHA ||
          this.menuItems[data.selectedIndex].id === MENU_ITEM_IDS.SEARCH)
      ) {
        this.handleVerticalMenuEvent({
          action: 'active',
          data: { selectedIndex: data.selectedIndex },
        });
      } else {
        this.handleVerticalMenuEvent({
          action: 'no-action',
          data: { selectedIndex: data.selectedIndex },
        });
      }
    } else {
      if (this.dataService.forceOpenMyAha) {
        this.handleVerticalMenuEvent({
          action: 'active',
          data: { tabId: MENU_ITEM_IDS.MY_AHA },
        });
      } else {
        this.handleVerticalMenuEvent({
          action: 'inactive',
          data: {
            selectedIndex: this.findHomeIndex(),
          },
        });
      }
      if (this.selectedIndex) {
        this.localStorage.setItem(
          KEYS.CURRENT_TAB_ID,
          this.menuItems[this.selectedIndex].id
        );
        this.localStorage.setItem(
          KEYS.CURRENT_TAB_NAME,
          this.languageService.getTranslatedText(
            this.menuItems[this.selectedIndex].name
          )
        );
      }
    }
  }

  private handleVerticalMenuEvent(event: VerticalMenuEvent): void {
    if (event.data?.reload) {
      this.updateMenu();
      this.selectedIndex = this.findHomeIndex();
    }
    if (event.data?.tabId) {
      const index = this.menuItems.findIndex(
        (item) => item.id === event.data.tabId
      );
      if (index !== -1) {
        this.selectedIndex = index;
      }
      this.navService.setIndexContainer('tv-vertical-menu', this.selectedIndex);
      this.onClick(index);
    } else if (event.data?.selectedIndex > -1) {
      this.selectedIndex = event.data.selectedIndex;
      this.navService.setIndexContainer('tv-vertical-menu', this.selectedIndex);
    }

    if (event.action === 'open') {
      this.navService.focusContainer(
        'tv-vertical-menu',
        false,
        this.selectedIndex
      );
    }
    if (event.data?.isMenuActive) {
      this.isMenuActive = event.data.isMenuActive;
    }
    if (event.data?.shouldFocus) {
      const item = this.menuItems[this.selectedIndex];
      this.updateFocus(item);
    }

    switch (event.action) {
      case 'show':
        if (!this.showVerticalNav) this.showVerticalNav = true;
        break;
      case 'hide':
        if (this.showVerticalNav) this.showVerticalNav = false;
        break;
      case 'open':
        if (!this.isMenuOpen) {
          this.isNavActive = true;
          this.isMenuOpen = true;
        }
        break;
      case 'close':
        if (this.isMenuOpen) {
          this.isNavActive = false;
          this.isMenuOpen = false;
        }
        break;
      case 'active':
        if (!this.isMenuActive) this.isMenuActive = true;
        break;
      case 'inactive':
        if (this.isMenuActive) this.isMenuActive = false;
        break;
      default:
        break;
    }
  }

  private findHomeIndex(): number {
    return this.menuItems.findIndex(
      (item) =>
        item.name === MENU_ITEM_NAMES.HOME ||
        item.id === 'home' ||
        item.id === this.dataService.homeTab
    );
  }

  onFocus(index: number, isFocused: boolean = true): void {
    if (isFocused) {
      this.navService.setIndexContainer('tv-vertical-menu', index);
      this.lastFocusedIndex = index;
      this.focusedIndex = index;
    } else {
      // if (this.selectedIndex === index) {
      //   this.focusedIndex = null;
      // }
      this.focusedIndex = null;
    }
  }

  onClick(index: number): void {
    let replaceUrl = false;
    if (
      this.selectedIndex === index ||
      this.menuItems[index].id === MENU_ITEM_IDS.SUBSCRIBE ||
      this.menuItems[index].id === MENU_ITEM_IDS.LINK_TV
    ) {
      this.localStorage.setItem(LocalStorageKeys.TAB_CHANGED, 'false');
    } else {
      replaceUrl = true;
      this.simplePlayerService.resetEvent();
      this.localStorage.setItem(LocalStorageKeys.TAB_CHANGED, 'true');
      this.selectedIndex = index;
      if (this.menuItems[index].id === MENU_ITEM_IDS.MY_AHA) {
        this.dataService.isAnonySettingsClicked = false;
        this.dataService.isAnonyLinkTvClicked = false;
      }
    }
    const event: VerticalMenuEvent = {
      action: 'close',
      data: {
        selectedIndex: this.selectedIndex,
        shouldFocus: true,
      },
    };
    this.dataService.emitVerticalMenuEvent(event);
    const item = this.menuItems[index];
    this.dataService.myahaContentIndex = null;
    if (
      [
        'subscribe',
        'link-tv',
        'my-aha',
        'language-selection',
        'search',
      ].includes(item.id)
    ) {
      this.tabService.currentTabName = { en: item.id };
      this.localStorage.setItem(KEYS.CURRENT_TAB_ID, item.id);
      this.localStorage.setItem(
        KEYS.CURRENT_TAB_NAME,
        this.languageService.getTranslatedText(item.name)
      );
      this.sendNewrelicEvent(true, item.id);
    } else {
      this.localStorage.setItem(KEYS.CURRENT_TAB_ID, item.id);
      this.localStorage.setItem(
        KEYS.CURRENT_TAB_NAME,
        this.languageService.getTranslatedText(item.name)
      );
      this.sendNewrelicEvent(false, item.name);
    }
    this.router.navigate([item.id], {
      state: { tabId: item.id },
      replaceUrl: replaceUrl,
    });
  }

  updateFocus(item: TabVm): void {
    switch (item?.id.toLowerCase()) {
      case MENU_ITEM_IDS.LINK_TV:
        this.navService.focusContainer('link-tv-container', false, 0);
        break;
      case MENU_ITEM_IDS.MY_AHA:
        const ahaContentIndex = this.dataService.myahaContentIndex;
        if (ahaContentIndex === 0) {
          this.navService.focusContainer('bookmarks-container', true, 0);
        } else if (ahaContentIndex === 1) {
          this.navService.focusContainer('watchlists-container', true, 0);
        } else if (ahaContentIndex === 2) {
          this.navService.focusContainer('rentals-container', true, 0);
        } else {
          const profilesElement =
            document.getElementsByClassName('profiles')[0];
          const buttonContainerElement =
            document.getElementsByClassName('explore-container')[0];
          if (profilesElement && buttonContainerElement) {
            this.navService.focusContainer(
              'explore-container',
              false,
              0,
              false
            );
          } else if (buttonContainerElement) {
            this.navService.focusContainer(
              'explore-container',
              false,
              0,
              false
            );
          } else if (profilesElement) {
            this.navService.focusContainer('top-panel', false, 0);
            this.navService.setIndexContainer('top-panel', 0);
          } else {
            this.navService.focusContainer('button-container', true, 0);
          }
        }
        break;
      case MENU_ITEM_IDS.SEARCH:
        if (this.dataService.searchFocus) {
          this.navService.focusContainer(
            this.dataService.searchFocus.element,
            false,
            this.dataService.searchFocus.index
          );
          this.dataService.searchFocus = null;
        } else if (this.lastFocusedClass) {
          this.navService.focusContainer(
            this.lastFocusedClass.element,
            false,
            this.lastFocusedClass.index
          );
          this.lastFocusedClass = null;
        } else {
          this.focusRestorationService.restoreFocusWithFallback(
            '.alphanumeric-keypad',
            {
              containerSelector: '.items-wrapper',
              resetFocusPosition: true,
            }
          );
        }
        break;
      default:
        if (
          this.localStorage.getItem(LocalStorageKeys.TAB_CHANGED) === 'true'
        ) {
          this.navService.focusContainer('carousel-track', true, 0);
        } else {
          this.navService.setIndexContainer(
            `horizontal-carousel-cards-${this.dataService.verticalIndex}`,
            this.dataService.horizontalIndex
          );
          this.navService.focusContainer(
            `horizontal-carousel-cards-${this.dataService.verticalIndex}`,
            true,
            this.dataService.horizontalIndex,
            false
          );
        }
    }
  }

  onSubscribe(): void {
    if (this.showPlanAction === SubscriptionAction.UPGRADE) {
      this.dataService.emitVerticalMenuEvent({
        action: 'close',
        data: {
          selectedIndex: this.selectedIndex,
        },
      });
      this.dialogService.openDialog(GenericAhaGoldPromptComponent, {
        data: {
          redirectFrom: 'vertical-menu',
          selectedIndex: this.selectedIndex,
        },
      });
    } else {
      this.dataService.emitVerticalMenuEvent({
        action: 'close',
        data: {
          selectedIndex: this.selectedIndex,
        },
      });
      this.dataService.previousUrl = this.router.url;
      this.router.navigate(['/plans']);
    }
  }

  focusRight() {
    this.localStorage.setItem(LocalStorageKeys.TAB_CHANGED, 'false');
    this.navService.setIndexContainer('tv-vertical-menu', this.selectedIndex);
    this.focusedIndex = null;
    this.dataService.emitVerticalMenuEvent({
      action: 'close',
      data: {
        shouldFocus: true,
      },
    });
  }

  onBack(): void {
    if (this.restrictBack) return;
    //find current focusable container
    const lastFocusedContainer = document.activeElement.closest(
      '.horizontal-focusable-container'
    );

    if (lastFocusedContainer) {
      if (!this.lastFocusedClass) {
        this.lastFocusedClass = { element: '', index: 0 };
      }
      this.lastFocusedClass.element =
        lastFocusedContainer.classList[0].toLowerCase();
      const dataIndex = lastFocusedContainer.getAttribute('data-index');
      this.lastFocusedClass.index = dataIndex ? Number(dataIndex) : 0;
    }
    this.restrictBack = true;
    if (this.isMenuOpen || !this.isMenuActive) {
      if (!this.dialogService.hasOpenDialog()) {
        this.dialogService
          .openDialogWithResult(ExitAppComponent)
          .pipe(takeUntil(this.destroy$))
          .subscribe((result) => {
            if (result) {
              if (this.isMenuOpen) {
                const index = this.lastFocusedIndex ?? this.selectedIndex;
                this.navService.focusContainer(
                  'tv-vertical-menu',
                  false,
                  index
                );
              }
            }
          });
      }
    } else {
      this.dataService.emitVerticalMenuEvent({
        action: 'open',
      });
    }
    this.restrictBack = false;
  }

  getTranslatedSubscriptionName() {
    return this.showPlanAction === SubscriptionAction.UPGRADE
      ? 'upgrade'
      : 'subscribe';
  }

  ngOnDestroy(): void {
    this.cleanup();
    this.forceClean();
  }

  private forceClean(): void {
    this.menuItems = null;
    this.isMenuOpen = false;
    this.isMenuActive = false;
    this.showVerticalNav = true;
    this.selectedIndex = null;
    this.focusedIndex = null;
    this.lastFocusedIndex = null;
    this.isNavActive = false;
    this.showPlanAction = null;
    this.lastFocusedClass = null;
    this.lang = null;
  }

  trackByMenuItemId(index: number, item: any): any {
    return item.id;
  }

  private sendNewrelicEvent(isTab: boolean, tabName: string): void {
    this.newrelic.sendAppEvent(
      isTab ? AppEventName.MENU_TAB_CHANGE : AppEventName.STOREFRONT_TAB_CHANGE,
      {
        pageID: isTab
          ? this.localStorage.getItem(KEYS.CURRENT_TAB_NAME)
          : PageType.Browse,
        previous_screen:
          typeof this.navigationService.currentScreen === 'string'
            ? this.navigationService.currentScreen
            : this.navigationService.currentScreen?.[this.lang] || undefined,
        current_screen: tabName,
        tabName: this.localStorage.getItem(KEYS.CURRENT_TAB_NAME),
        tabId: this.localStorage.getItem(KEYS.CURRENT_TAB_ID),
        storefrontId: !isTab
          ? this.localStorage.getItem(KEYS.STOREFRONT_ID)
          : undefined,
      }
    );
  }
}
